import { useCallback, useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { FEATURES } from '../constants/features.tsx';
import type { useCommsFeaturesQuery } from './__generated__/useCommsFeaturesQuery.graphql.ts';

export const useCommsFeatures = (): {
	loading: boolean;
	features: {
		[FEATURES.STATUS_PAGES]: boolean;
		[FEATURES.STATUS_PAGES_DRAFT_ONLY]: boolean;
		[FEATURES.STATUS_PAGES_SEND_UPDATE]: boolean;
		[FEATURES.SUGGEST_STAKEHOLDERS]: boolean;
	};
} => {
	const relayEnvironment = useRelayEnvironment();

	const isAdmin = useIsAdmin();
	const { isOpsAdmin } = useOgTenantInfo();
	const appEditions = useAppEditions();
	const cloudId = useCloudId();

	const [loading, setLoading] = useState(false);
	const [isEnterpriseFromLicense, setIsEnterpriseFromLicense] = useState(false);

	const isPremiumJsm = appEditions.serviceDesk === 'PREMIUM_EDITION';

	const fetchSiteTransformerData = useCallback(async () => {
		try {
			setLoading(true);
			const res = await fetchQuery<useCommsFeaturesQuery>(
				relayEnvironment,
				graphql`
					query useCommsFeaturesQuery($cloudId: String) {
						stakeholderComms_getStakeholderCommsTransformerRecord(cloudId: $cloudId) {
							transformerRecord {
								jsmProductDetail {
									license
								}
							}
						}
					}
				`,
				{ cloudId },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();
			setIsEnterpriseFromLicense(
				res?.stakeholderComms_getStakeholderCommsTransformerRecord?.transformerRecord
					?.jsmProductDetail?.license === 'enterprise',
			);
		} catch {
			// err
		} finally {
			setLoading(false);
		}
	}, [relayEnvironment, cloudId]);

	const shouldSkipApiCall =
		expVal<boolean>('communications_status_pages_enterprise_feature', 'value', false) ||
		!isPremiumJsm;

	useEffect(() => {
		if (shouldSkipApiCall) {
			return;
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect
		fetchSiteTransformerData();
	}, [shouldSkipApiCall, fetchSiteTransformerData]);

	const isEnterprise =
		expVal<boolean>('communications_status_pages_enterprise_feature', 'value', false) ||
		isEnterpriseFromLicense;

	const enterpriseStatusPages =
		isEnterprise && (isOpsAdmin || isAdmin) && fg('communications_status_page_manage_platform');

	const isPremiumAdmin = appEditions.serviceDesk === 'PREMIUM_EDITION' && (isOpsAdmin || isAdmin);

	let statusPagesDraftOnly = false;
	let statusPages = enterpriseStatusPages;
	if (fg('status_page_premium_exp_kill_switch')) {
		const isPremiumEligible =
			isPremiumAdmin &&
			expVal<boolean>('communications_status_page_premium_exp_config', 'value', false);
		if (!loading && isPremiumEligible) {
			statusPagesDraftOnly = true;
		}
		if (!statusPages && isPremiumEligible) {
			statusPages = true;
		}
	}

	return {
		loading: shouldSkipApiCall ? false : loading,
		features: {
			[FEATURES.STATUS_PAGES_DRAFT_ONLY]: statusPagesDraftOnly,
			[FEATURES.STATUS_PAGES]: statusPages,
			[FEATURES.STATUS_PAGES_SEND_UPDATE]:
				isEnterprise && fg('communications_status_page_manage_platform'),
			[FEATURES.SUGGEST_STAKEHOLDERS]: appEditions.serviceDesk === 'PREMIUM_EDITION',
		},
	};
};
